import React, { useEffect, useState } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { Button, Modal, PageTitle, Picker } from '../../root/components/common'
import Input from '../../root/components/common/input'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from '../actions'
import { useHistory } from 'react-router-dom'
import { isEmailString } from '../../../utils'
import {
    getPickerCustomersForLocationCreationSelector,
    getPickerLocations
} from '../selectors'
import { isEmpty } from 'lodash'
import { useAddressApi } from '../../root/hooks/use-address-api'
import { Autocomplete } from '@material-ui/lab'

const useStyles = makeStyles(theme => ({
    root: {
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
    },
    pageTitle: {
        marginBottom: 40
    },
    row: {
        display: 'flex',
        margin: '16px 0',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    section: {
        marginBottom: 16
    },
    autocompleteSection: {
        marginBottom: 6
    },
    rowTitle: {
        marginRight: 15,
        flex: 1,
        alignItems: 'center'
    },
    picker: {
        border: theme.border,
        padding: '0 8px',
        borderRadius: 7,
        width: '150px',
        flex: 1
    },
    btnsWrapper: {
        marginTop: 20,
        display: 'flex',
        justifyContent: 'flex-end'
    }
}))

export const LocationCreateModal = ({
    onClose,
    parentId,
    pickerLocations,
    parentLocation,
    withCustomerSelection,
    modalProps: { open }
}) => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()

    const { locale } = useSelector(state => state.main)
    const { locations } = useSelector(state => state.locations)
    const pickerCustomers = useSelector(
        getPickerCustomersForLocationCreationSelector
    )
    const {
        foundAddresses,
        isLoadingAddress,
        addressSearchString,
        setAddressSearchString,
        clearSearchString
    } = useAddressApi()

    const [name, setName] = useState(locale.locations.newLocation)
    const [customerId, setCustomerId] = useState('')
    const [pId, setPId] = useState(parentId || '')
    const [locationsPickerOptions, setLocationsPickerOptions] = useState(
        withCustomerSelection
            ? {}
            : { ...pickerLocations, [parentLocation.id]: parentLocation.name }
    )
    const [address, setAddress] = useState('')
    const [contactMail, setContactMail] = useState('')
    const [contactName, setContactName] = useState('')
    const [contactPhone, setContactPhone] = useState('')

    const isNameError = name.length === 0
    const isCustomerIdError = withCustomerSelection && !customerId
    const isLocationsPickerDisabled =
        !withCustomerSelection || (withCustomerSelection && !customerId)
    const isParentIdError = !isLocationsPickerDisabled && !pId

    const save = () => {
        const data = {
            name,
            address,
            contact_name: contactName,
            contact_email: contactMail,
            contact_phone_number: contactPhone,
            parent_id: Number(pId),
            customer_id: withCustomerSelection
                ? Number(customerId)
                : Number(parentLocation.customer_id),
            owner_id: withCustomerSelection
                ? locations.find(l => l.id === Number(pId))?.owner_id || null
                : Number(parentLocation.owner_id)
        }
        dispatch(actions.locationCreateRequest(data, history))
        onClose()
    }

    useEffect(() => {
        if (withCustomerSelection && customerId) {
            setPId('')
            setLocationsPickerOptions(
                getPickerLocations(locations, {
                    id: 0,
                    customer_id: customerId
                })
            )
        }
    }, [withCustomerSelection, customerId])

    return (
        <Modal size="md" onClose={onClose} open={open} className={classes.root}>
            <PageTitle
                title={locale.common.createNew}
                className={classes.pageTitle}
            />
            <Input
                label={locale.common.name}
                type="text"
                setValue={setName}
                value={name}
                autoFocus
                errorText={locale.locations.nameCanNotBeEmpty}
                isError={isNameError}
                onEnter={save}
            />
            {withCustomerSelection && (
                <div
                    className={classes.row}
                    style={{ width: '60%', marginTop: 26 }}
                >
                    <div className={classes.rowTitle}>
                        {locale.common.organisation}:
                    </div>
                    <Picker
                        className={classes.picker}
                        onChange={setCustomerId}
                        value={customerId}
                        options={pickerCustomers}
                        isError={isCustomerIdError}
                    />
                </div>
            )}
            <div className={classes.row} style={{ width: '60%' }}>
                <div className={classes.rowTitle}>
                    {locale.locations.parentLocation}:
                </div>
                <Picker
                    className={classes.picker}
                    disabled={isLocationsPickerDisabled}
                    onChange={setPId}
                    value={pId}
                    options={locationsPickerOptions}
                    isError={isParentIdError}
                />
            </div>
            <h3
                style={{
                    fontSize: 16,
                    fontWeight: 'normal'
                }}
            >
                {locale.locations.locationDetails}
            </h3>
            <Autocomplete
                options={foundAddresses}
                noOptionsText={locale.detachedSharing.nothingFound}
                className={classes.autocompleteSection}
                renderInput={params => {
                    return (
                        <Input
                            type="text"
                            {...params}
                            label={locale.locations.address}
                            variant="outlined"
                            value={addressSearchString}
                            setValue={v => {
                                setAddress(v)
                                setAddressSearchString(v)
                            }}
                            debounce={300}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoadingAddress ? (
                                            <CircularProgress size={20} />
                                        ) : null}
                                        {address &&
                                            params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )
                }}
                onChange={(e, newValue) => {
                    setAddress(newValue)

                    if (!newValue) {
                        clearSearchString()
                    }
                }}
                value={address}
            />
            <Input
                className={classes.section}
                label={locale.locations.contact}
                type="text"
                setValue={setContactName}
                value={contactName}
                onEnter={save}
            />
            <Input
                className={classes.section}
                label={locale.locations.contactPhone}
                type="text"
                setValue={setContactPhone}
                value={contactPhone}
                onEnter={save}
            />
            <Input
                className={classes.section}
                label={locale.locations.locationEmail}
                type="text"
                setValue={setContactMail}
                value={contactMail}
                onEnter={save}
                isError={contactMail.length > 0 && !isEmailString(contactMail)}
                errorText={locale.common.notValidEmail}
            />
            <div className={classes.btnsWrapper}>
                <Button
                    style={{ marginRight: 8 }}
                    onClick={save}
                    disabled={
                        isNameError ||
                        (contactMail.length > 0 &&
                            !isEmailString(contactMail)) ||
                        isCustomerIdError ||
                        isParentIdError
                    }
                >
                    {locale.common.save}
                </Button>
                <Button reverted onClick={onClose}>
                    {locale.common.cancel}
                </Button>
            </div>
        </Modal>
    )
}
